import React from 'react';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/hooks/useAuth';
import { LazyImage } from '@/components/LazyImage';
import { useTranslation } from '@/hooks/useTranslation';

interface Tour {
  id: string;
  title: string;
  city: string;
  country: string;
  image?: string;
  style?: string;
  description?: string;
  duration?: string;
  museum_count?: number;
  latitude?: number;
  longitude?: number;
  created_at?: string;
  name_places?: any[];
  Latitude_places?: number[];
  Longitude_places?: number[];
}

interface TourPageCarouselProps {
  tours: Tour[];
  currentTourIndex: number;
  onTourSelect: (tourId: string, index: number) => void;
  onDetailsSelect?: (tourId: string, index: number) => void;
  desktopCarouselRef?: React.RefObject<HTMLDivElement>;
}

const TourPageCarousel: React.FC<TourPageCarouselProps> = ({
  tours,
  currentTourIndex,
  onTourSelect,
  onDetailsSelect,
  desktopCarouselRef
}) => {
  const { toggleFavorite, isFavorite } = useFavorites();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const handleFavoriteClick = (e: React.MouseEvent, tourName: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      window.location.href = '/auth';
      return;
    }
    
    toggleFavorite(tourName);
  };

  if (!tours.length) {
    return null;
  }

  return (
    <div 
      ref={desktopCarouselRef}
      className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide px-6"
      style={{ scrollBehavior: 'smooth' }}
    >
      {tours.map((tour, index) => (
        <div 
          key={`${tour.id}-${index}`}
          data-tour-id={tour.id}
          className={`flex-shrink-0 w-64 md:w-72 group transition-all duration-200 ${
            index === currentTourIndex ? 'ring-2 ring-primary rounded-xl' : ''
          }`}
        >
          {/* Image Container with Overlay Text */}
          <div className="relative h-36 md:h-40">
            {/* Clickable background layer */}
            <div 
              className="absolute inset-0 cursor-pointer z-0"
              onClick={() => onTourSelect(tour.id, index)}
            />
            
            {tour.image ? (
              <LazyImage
                src={tour.image}
                alt={tour.title}
                className="w-full h-full object-cover rounded-t-xl pointer-events-none"
                placeholder={
                  <div className="w-full h-full bg-gray-200 animate-pulse rounded-t-xl" />
                }
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-300 rounded-t-xl flex items-center justify-center pointer-events-none">
                <span className="text-gray-600 text-3xl font-bold">
                  {tour.title.charAt(0)}
                </span>
              </div>
            )}
            
            {/* Overlay gradient for better text visibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent rounded-t-xl pointer-events-none z-[1]" />
            
            {/* City/Country Text Overlay */}
            <div className="absolute top-3 left-3 right-12 z-[5] pointer-events-none">
              <h3 className="font-bold text-white text-base md:text-lg drop-shadow-lg truncate">
                {tour.title}
              </h3>
              <p className="text-white/90 text-sm drop-shadow-lg truncate">{tour.country}</p>
            </div>
            
            {/* Favorite Heart */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => handleFavoriteClick(e, tour.title)}
              className={`absolute top-3 right-3 w-7 h-7 p-0 rounded-full backdrop-blur-sm transition-all duration-200 hover:scale-110 z-[10] ${
                isFavorite(tour.title)
                  ? 'bg-white/95 text-red-500 hover:bg-white shadow-sm' 
                  : 'bg-black/30 text-white hover:bg-black/50'
              }`}
              aria-label={isFavorite(tour.title) ? `Retirer ${tour.title} des favoris` : `Ajouter ${tour.title} aux favoris`}
            >
              <Heart className={`w-3.5 h-3.5 ${isFavorite(tour.title) ? 'fill-current' : ''}`} />
            </Button>
            
            {/* Details Button */}
            <button
              onClick={() => {
                onDetailsSelect?.(tour.id, index);
              }}
              className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[10] px-6 py-2 bg-black/40 backdrop-blur-md text-white rounded-lg hover:bg-black/50 transition-all duration-200 font-medium text-sm w-32 cursor-pointer"
              aria-label={`Voir les détails du tour ${tour.title}`}
            >
              {t('details')}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TourPageCarousel;
